import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Layout/Header';
import api, { importJobsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  ArrowLeft, Upload, FileSpreadsheet, CheckCircle, 
  XCircle, AlertTriangle, Loader2, Info, ArrowRight,
  RefreshCw, Eye, Columns, Check, X, Edit2, KeyRound,
  Layers, Clock, Download, FileDown
} from 'lucide-react';

// Batch size for large file imports
const BATCH_SIZE = 10000;

// Available import templates
const IMPORT_TEMPLATES = [
  { id: 'standard', name: 'Standard HRMS Template', description: 'Basic employee import with essential fields' },
  { id: 'sap', name: 'SAP HR Template', description: 'Matches SAP HR export format' },
  { id: 'oracle', name: 'Oracle HR Template', description: 'Matches Oracle HCM export format' },
  { id: 'workday', name: 'Workday Template', description: 'Matches Workday export format' },
  { id: 'peoplesoft', name: 'PeopleSoft Template', description: 'Matches PeopleSoft HCM export format' },
];

// System fields that can be mapped
const SYSTEM_FIELDS = [
  { value: 'employee_code', label: 'Employee Code', required: true },
  { value: 'emp_code', label: 'Emp Code (Alt)', required: false },
  { value: 'first_name', label: 'First Name', required: false },
  { value: 'middle_name', label: 'Middle Name', required: false },
  { value: 'last_name', label: 'Last Name', required: false },
  { value: 'name', label: 'Full Name', required: false },
  { value: 'email', label: 'Email', required: true },
  { value: 'personal_email', label: 'Personal Email', required: false },
  { value: 'date_of_birth', label: 'Date of Birth', required: false },
  { value: 'gender', label: 'Gender', required: false },
  { value: 'date_of_joining', label: 'Date of Joining', required: false },
  { value: 'date_of_appointment', label: 'Date of Appointment', required: false },
  { value: 'designation', label: 'Designation', required: true },
  { value: 'department', label: 'Department', required: false },
  { value: 'department_id', label: 'Department ID', required: false },
  { value: 'mobile_primary', label: 'Mobile (Primary)', required: true },
  { value: 'mobile', label: 'Mobile (Alt)', required: false },
  { value: 'phone', label: 'Phone (Alt)', required: false },
  { value: 'mobile_secondary', label: 'Mobile (Secondary)', required: false },
  { value: 'employment_type', label: 'Employment Type', required: false },
  { value: 'grade', label: 'Grade', required: false },
  { value: 'basic_pay', label: 'Basic Pay/Salary', required: false },
  { value: 'pay_level', label: 'Pay Level', required: false },
  { value: 'pay_scale', label: 'Pay Scale', required: false },
  { value: 'father_name', label: 'Father\'s Name', required: false },
  { value: 'mother_name', label: 'Mother\'s Name', required: false },
  { value: 'spouse_name', label: 'Spouse Name', required: false },
  { value: 'marital_status', label: 'Marital Status', required: false },
  { value: 'nationality', label: 'Nationality', required: false },
  { value: 'religion', label: 'Religion', required: false },
  { value: 'blood_group', label: 'Blood Group', required: false },
  { value: 'aadhaar_number', label: 'Aadhaar Number', required: false },
  { value: 'pan_number', label: 'PAN Number', required: false },
  { value: 'passport_number', label: 'Passport Number', required: false },
  { value: 'voter_id', label: 'Voter ID', required: false },
  { value: 'driving_license', label: 'Driving License', required: false },
  { value: 'epf_number', label: 'EPF Number', required: false },
  { value: 'uan_number', label: 'UAN Number', required: false },
  { value: 'esi_number', label: 'ESI Number', required: false },
  { value: 'gpf_number', label: 'GPF Number', required: false },
  { value: 'pran_number', label: 'PRAN Number', required: false },
  { value: 'bank_account', label: 'Bank Account', required: false },
  { value: 'bank_name', label: 'Bank Name', required: false },
  { value: 'ifsc_code', label: 'IFSC Code', required: false },
  { value: 'address', label: 'Address', required: false },
  { value: 'city', label: 'City', required: false },
  { value: 'state', label: 'State', required: false },
  { value: 'pincode', label: 'Pincode', required: false },
  { value: 'reporting_manager', label: 'Reporting Manager', required: false },
  { value: 'cadre', label: 'Cadre', required: false },
  { value: '__skip__', label: '-- Skip this column --', required: false },
];

const EmployeeMasterImport = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Workflow state
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview & Map, 3: Results
  
  // Step 1: File upload
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Step 2: Preview & Mapping
  const [preview, setPreview] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [editingColumn, setEditingColumn] = useState(null);
  
  // Step 3: Import results
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  
  // Batch processing state
  const [batchProgress, setBatchProgress] = useState({
    currentBatch: 0,
    totalBatches: 0,
    processedRows: 0,
    totalRows: 0,
    successCount: 0,
    failedCount: 0,
    isProcessing: false
  });
  
  // Template info
  const [templateInfo, setTemplateInfo] = useState(null);
  
  useEffect(() => {
    fetchTemplateInfo();
  }, []);

  const fetchTemplateInfo = async () => {
    try {
      const res = await api.get('/employee-master/import/template');
      setTemplateInfo(res.data);
    } catch (error) {
      console.error('Failed to fetch template info');
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  };

  const validateAndSetFile = (selectedFile) => {
    const validTypes = ['.xlsx', '.xls', '.csv'];
    const isValid = validTypes.some(ext => selectedFile.name.toLowerCase().endsWith(ext));
    
    if (!isValid) {
      toast.error('Please select an Excel (.xlsx, .xls) or CSV file');
      return;
    }
    
    setFile(selectedFile);
    setPreview(null);
    setColumnMapping({});
    setResults(null);
  };

  const handlePreview = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/employee-master/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPreview(res.data);
      setColumnMapping(res.data.header_mapping || {});
      setStep(2);
      
      if (res.data.can_import) {
        toast.success('File analyzed successfully! Review the column mappings below.');
      } else {
        toast.warning('Some required fields are missing. Please map them manually.');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to preview file';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleMappingChange = (rawHeader, newSystemField) => {
    setColumnMapping(prev => ({
      ...prev,
      [rawHeader]: newSystemField === '__skip__' ? null : newSystemField
    }));
    setEditingColumn(null);
  };

  const getMissingRequired = () => {
    const mappedFields = Object.values(columnMapping).filter(Boolean);
    const missing = [];
    
    // Check for name (either 'name' or 'first_name' is needed)
    if (!mappedFields.includes('name') && !mappedFields.includes('first_name')) {
      missing.push('name OR first_name');
    }
    
    // Check for employee_code or alternatives (emp_code, id)
    if (!mappedFields.includes('employee_code') && !mappedFields.includes('emp_code') && !mappedFields.includes('id')) {
      missing.push('employee_code');
    }
    
    // Check for email
    if (!mappedFields.includes('email')) {
      missing.push('email');
    }
    
    // Check for designation
    if (!mappedFields.includes('designation')) {
      missing.push('designation');
    }
    
    // Check for mobile (mobile_primary, mobile, or phone)
    if (!mappedFields.includes('mobile_primary') && !mappedFields.includes('mobile') && !mappedFields.includes('phone')) {
      missing.push('mobile_primary');
    }
    
    return missing;
  };

  const canProceed = () => {
    const missing = getMissingRequired();
    return missing.length === 0;
  };

  // Parse file into rows for batch processing
  const parseFileForBatching = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          // For Excel files, we need SheetJS which is loaded in the backend
          // So we'll use the preview data that's already parsed
          if (preview && preview.sample_data) {
            resolve(preview.sample_data);
          } else {
            resolve([]);
          }
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleImport = async () => {
    if (!canProceed()) {
      toast.error('Please map all required fields before importing');
      return;
    }
    
    const totalRows = preview?.total_rows || preview?.sample_data?.length || 0;
    const totalBatches = Math.ceil(totalRows / BATCH_SIZE);
    
    // If file is small enough, do a single import
    if (totalRows <= BATCH_SIZE) {
      await handleSingleImport();
      return;
    }
    
    // For large files, process in batches
    setImporting(true);
    setBatchProgress({
      currentBatch: 0,
      totalBatches,
      processedRows: 0,
      totalRows,
      successCount: 0,
      failedCount: 0,
      isProcessing: true
    });
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('custom_mapping', JSON.stringify(columnMapping));
      
      // Process file - backend will handle batching internally
      const res = await api.post('/employee-master/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setBatchProgress(prev => ({
            ...prev,
            uploadProgress: percentCompleted
          }));
        }
      });
      
      // Update final results
      const aggregatedResults = {
        total: res.data.total,
        success: res.data.success,
        failed: res.data.failed,
        errors: res.data.errors || [],
        imported_codes: res.data.imported_codes || [],
        login_ids_created: res.data.login_ids_created || [],
        incomplete_profiles: res.data.incomplete_profiles || [],
        truncated: res.data.truncated,
        original_total: res.data.original_total,
        warning: res.data.warning
      };
      
      setResults(aggregatedResults);
      setStep(3);
      
      if (aggregatedResults.success > 0) {
        toast.success(`Successfully imported ${aggregatedResults.success} employees`);
      }
      if (aggregatedResults.failed > 0) {
        toast.warning(`${aggregatedResults.failed} records failed to import`);
      }
      if (aggregatedResults.truncated) {
        toast.info(`File was truncated: ${aggregatedResults.warning}`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Import failed';
      toast.error(errorMsg);
    } finally {
      setImporting(false);
      setBatchProgress(prev => ({ ...prev, isProcessing: false }));
    }
  };

  const handleSingleImport = async () => {
    setImporting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('custom_mapping', JSON.stringify(columnMapping));
      
      const res = await api.post('/employee-master/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setResults(res.data);
      setStep(3);
      
      if (res.data.success > 0) {
        toast.success(`Successfully imported ${res.data.success} employees`);
      }
      if (res.data.failed > 0) {
        toast.warning(`${res.data.failed} records failed to import`);
      }
      if (res.data.truncated) {
        toast.info(`Note: ${res.data.warning}`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Import failed';
      toast.error(errorMsg);
    } finally {
      setImporting(false);
    }
  };

  const resetWorkflow = () => {
    setStep(1);
    setFile(null);
    setPreview(null);
    setColumnMapping({});
    setResults(null);
    setBatchProgress({
      currentBatch: 0,
      totalBatches: 0,
      processedRows: 0,
      totalRows: 0,
      successCount: 0,
      failedCount: 0,
      isProcessing: false
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Progress bar component for batch imports
  const BatchProgressBar = () => {
    if (!batchProgress.isProcessing && !importing) return null;
    
    const totalRows = preview?.total_rows || preview?.sample_data?.length || 0;
    const progressPercent = batchProgress.uploadProgress || 0;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-full max-w-md mx-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              Importing Employees
            </CardTitle>
            <CardDescription>
              Please wait while we process your file...
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Upload Progress</span>
                <span className="font-medium">{progressPercent}%</span>
              </div>
              <Progress value={progressPercent} className="h-2" />
            </div>
            
            {totalRows > BATCH_SIZE && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-2 text-blue-700">
                  <Layers className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    Large file detected ({totalRows.toLocaleString()} rows)
                  </span>
                </div>
                <p className="text-xs text-blue-600 mt-1">
                  Processing up to {BATCH_SIZE} rows per batch to ensure stability.
                </p>
              </div>
            )}
            
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Clock className="w-4 h-4" />
              <span>This may take a few moments...</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Step indicator component
  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-6">
      {[
        { num: 1, label: 'Upload File' },
        { num: 2, label: 'Map Columns' },
        { num: 3, label: 'Results' }
      ].map((s, idx) => (
        <React.Fragment key={s.num}>
          <div className={`flex items-center ${step >= s.num ? 'text-blue-600' : 'text-slate-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 
              ${step > s.num ? 'bg-blue-600 border-blue-600 text-white' : 
                step === s.num ? 'border-blue-600 text-blue-600' : 'border-slate-300'}`}>
              {step > s.num ? <Check className="w-4 h-4" /> : s.num}
            </div>
            <span className="ml-2 text-sm font-medium hidden sm:inline">{s.label}</span>
          </div>
          {idx < 2 && (
            <div className={`w-12 sm:w-20 h-0.5 mx-2 ${step > s.num ? 'bg-blue-600' : 'bg-slate-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div data-testid="employee-master-import">
      {/* Batch Progress Overlay */}
      <BatchProgressBar />
      
      <Header 
        title={
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/employee-master')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <span>Smart Employee Import</span>
          </div>
        }
        subtitle="Automatically detect and map columns from any Excel or CSV file"
      />

      <div className="p-6 space-y-6 max-w-5xl mx-auto">
        <StepIndicator />

        {/* STEP 1: Upload File */}
        {step === 1 && (
          <>
            {/* Smart Import Info */}
            <Card className="border-blue-200 bg-blue-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-800">
                  <Info className="w-5 h-5" />
                  Smart Column Detection
                </CardTitle>
              </CardHeader>
              <CardContent className="text-blue-700 space-y-3">
                <p>
                  This import wizard automatically detects columns from your Excel/CSV file based on header names. 
                  No fixed template required!
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium mb-1">✓ Supported column names (examples):</p>
                    <ul className="list-disc list-inside text-blue-600 space-y-0.5">
                      <li>"Employee Code", "Emp Code", "Emp ID", "Staff ID"</li>
                      <li>"Name", "Full Name", "First Name", "Last Name"</li>
                      <li>"Email", "Email ID", "Official Email"</li>
                      <li>"Mobile", "Phone", "Contact Number"</li>
                      <li>"DOJ", "Joining Date", "Date of Joining"</li>
                    </ul>
                  </div>
                  <div>
                    <p className="font-medium mb-1">✓ Features:</p>
                    <ul className="list-disc list-inside text-blue-600 space-y-0.5">
                      <li>Case-insensitive matching</li>
                      <li>Handles spaces, underscores, hyphens</li>
                      <li>Splits "Full Name" into parts automatically</li>
                      <li>Flexible date formats (DD/MM/YYYY, YYYY-MM-DD, etc.)</li>
                      <li>Department lookup by name or ID</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Download Templates */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileDown className="w-5 h-5 text-green-600" />
                  Download Import Templates
                </CardTitle>
                <CardDescription>
                  Pre-built templates for popular HR systems - download and fill with your data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {IMPORT_TEMPLATES.map((template) => (
                    <div 
                      key={template.id}
                      className="p-4 border rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-slate-800 truncate">{template.name}</h4>
                          <p className="text-xs text-slate-500 mt-1">{template.description}</p>
                        </div>
                        <a
                          href={`${api.defaults.baseURL}/import-jobs/templates/${template.id}/download`}
                          download
                          className="ml-2 p-2 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-colors flex-shrink-0"
                          data-testid={`download-template-${template.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-3">
                  Templates include sample data and instructions. Maximum 10,000 rows per import.
                </p>
              </CardContent>
            </Card>

            {/* Upload Area */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-blue-600" />
                  Upload Your File
                </CardTitle>
                <CardDescription>
                  Upload any Excel (.xlsx, .xls) or CSV file with employee data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                    file ? 'border-emerald-300 bg-emerald-50' : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50'
                  }`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-dropzone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  
                  {file ? (
                    <div className="space-y-2">
                      <FileSpreadsheet className="w-12 h-12 mx-auto text-emerald-600" />
                      <p className="font-medium text-emerald-700">{file.name}</p>
                      <p className="text-sm text-slate-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-12 h-12 mx-auto text-slate-400" />
                      <p className="font-medium text-slate-600">
                        Drag and drop your file here, or click to browse
                      </p>
                      <p className="text-sm text-slate-400">
                        Supports Excel (.xlsx, .xls) and CSV files
                      </p>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-between items-center">
                  <Button variant="outline" onClick={() => navigate('/employee-master')}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handlePreview} 
                    disabled={!file || uploading}
                    data-testid="preview-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-2" />
                        Preview & Map Columns
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* STEP 2: Preview & Column Mapping */}
        {step === 2 && preview && (
          <>
            {/* File Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                  {preview.filename}
                </CardTitle>
                <CardDescription>
                  {preview.total_columns} columns detected • {preview.sample_data?.length || 0} sample rows shown
                </CardDescription>
              </CardHeader>
            </Card>

            {/* Mapping Status */}
            {getMissingRequired().length > 0 && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Missing Required Fields</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Please map the following required fields: {' '}
                        <span className="font-mono">{getMissingRequired().join(', ')}</span>
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Column Mapping Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Columns className="w-5 h-5 text-blue-600" />
                  Column Mapping
                </CardTitle>
                <CardDescription>
                  Review and adjust how your file columns map to system fields. Click on any mapping to change it.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="mapping-table">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3 font-medium">Your Column</th>
                        <th className="text-left p-3 font-medium">Maps To</th>
                        <th className="text-left p-3 font-medium">Sample Data</th>
                        <th className="text-center p-3 font-medium w-20">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.raw_headers?.map((rawHeader, idx) => {
                        const mappedField = columnMapping[rawHeader];
                        const fieldInfo = SYSTEM_FIELDS.find(f => f.value === mappedField);
                        const isRequired = fieldInfo?.required;
                        const sampleValue = preview.sample_data?.[0]?.[idx];
                        
                        return (
                          <tr key={idx} className="border-b hover:bg-slate-50">
                            <td className="p-3 font-mono text-slate-700">{rawHeader}</td>
                            <td className="p-3">
                              {editingColumn === rawHeader ? (
                                <Select
                                  value={mappedField || '__skip__'}
                                  onValueChange={(val) => handleMappingChange(rawHeader, val)}
                                >
                                  <SelectTrigger className="w-48" data-testid={`mapping-select-${idx}`}>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {SYSTEM_FIELDS.map(field => (
                                      <SelectItem key={field.value} value={field.value}>
                                        {field.label} {field.required && <span className="text-red-500">*</span>}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <button
                                  onClick={() => setEditingColumn(rawHeader)}
                                  className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
                                  data-testid={`edit-mapping-${idx}`}
                                >
                                  {mappedField ? (
                                    <span className={`${isRequired ? 'text-blue-700 font-medium' : 'text-slate-700'}`}>
                                      {fieldInfo?.label || mappedField}
                                      {isRequired && <span className="text-red-500 ml-1">*</span>}
                                    </span>
                                  ) : (
                                    <span className="text-slate-400 italic">Skipped</span>
                                  )}
                                  <Edit2 className="w-3 h-3 text-slate-400" />
                                </button>
                              )}
                            </td>
                            <td className="p-3 text-slate-500 max-w-xs truncate">
                              {sampleValue !== null && sampleValue !== undefined ? String(sampleValue) : '-'}
                            </td>
                            <td className="p-3 text-center">
                              {mappedField ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700">
                                  <Check className="w-3 h-3 mr-1" /> Mapped
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-slate-100 text-slate-500">
                                  <X className="w-3 h-3 mr-1" /> Skip
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 flex justify-between items-center">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to Upload
                  </Button>
                  <Button 
                    onClick={handleImport} 
                    disabled={!canProceed() || importing}
                    data-testid="import-btn"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Import {preview.sample_data?.length ? `(${preview.sample_data.length}+ rows)` : ''}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Data Preview */}
            {preview.sample_data && preview.sample_data.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-blue-600" />
                    Data Preview
                  </CardTitle>
                  <CardDescription>
                    First {preview.sample_data.length} rows from your file
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="preview-table">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="p-2 text-left font-medium text-slate-500">#</th>
                          {preview.raw_headers?.map((header, idx) => (
                            <th key={idx} className="p-2 text-left font-medium whitespace-nowrap">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {preview.sample_data.map((row, rowIdx) => (
                          <tr key={`preview-row-${rowIdx}`} className="border-b hover:bg-slate-50">
                            <td className="p-2 text-slate-400">{rowIdx + 1}</td>
                            {row.map((cell, cellIdx) => (
                              <td key={`cell-${rowIdx}-${cellIdx}`} className="p-2 max-w-32 truncate">
                                {cell !== null && cell !== undefined ? String(cell) : '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* STEP 3: Results */}
        {step === 3 && results && (
          <>
            {/* Summary Card */}
            <Card data-testid="import-results">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {results.success > 0 ? (
                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-600" />
                  )}
                  Import Complete
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Truncation Warning */}
                {results.truncated && (
                  <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-amber-800">Large File Truncated</h4>
                        <p className="text-sm text-amber-700 mt-1">
                          Your file contained {results.original_total?.toLocaleString()} rows, but only {results.total?.toLocaleString()} were processed.
                        </p>
                        <p className="text-sm text-amber-600 mt-2">
                          To import the remaining rows, please split your file into smaller batches (max {BATCH_SIZE} rows each) and import them separately.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-slate-100 rounded-lg text-center">
                    <p className="text-3xl font-bold text-slate-700">{results.total}</p>
                    <p className="text-sm text-slate-500">Total Records</p>
                  </div>
                  <div className="p-4 bg-emerald-100 rounded-lg text-center">
                    <p className="text-3xl font-bold text-emerald-700">{results.success}</p>
                    <p className="text-sm text-emerald-600">Successful</p>
                  </div>
                  <div className="p-4 bg-red-100 rounded-lg text-center">
                    <p className="text-3xl font-bold text-red-700">{results.failed}</p>
                    <p className="text-sm text-red-600">Failed</p>
                  </div>
                </div>

                {/* Imported Employee Codes */}
                {results.imported_codes && results.imported_codes.length > 0 && (
                  <div>
                    <h4 className="font-medium text-emerald-700 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Imported Employee Codes ({results.imported_codes.length})
                    </h4>
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-3 bg-emerald-50 rounded-lg">
                      {results.imported_codes.map((code) => (
                        <span key={code} className="px-2 py-1 bg-emerald-200 text-emerald-800 rounded text-sm font-mono">
                          {code}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Login IDs Created */}
                {results.login_ids_created && results.login_ids_created.length > 0 && (
                  <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                    <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                      <KeyRound className="w-4 h-4" />
                      Login IDs Created ({results.login_ids_created.length})
                    </h4>
                    <p className="text-sm text-blue-700 mb-3">
                      User accounts have been auto-generated. Default password: <code className="bg-blue-100 px-1 rounded">password</code>
                    </p>
                    <div className="max-h-40 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-blue-600">
                            <th className="pb-2">Employee Code</th>
                            <th className="pb-2">Login ID</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-blue-100">
                          {results.login_ids_created.map((item) => (
                            <tr key={item.employee_code || item.login_id}>
                              <td className="py-1.5 font-mono text-slate-700">{item.employee_code}</td>
                              <td className="py-1.5 font-mono font-medium text-blue-800">{item.login_id}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Incomplete Profiles Warning */}
                {results.incomplete_profiles && results.incomplete_profiles.length > 0 && (
                  <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                    <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Incomplete Profiles ({results.incomplete_profiles.length})
                    </h4>
                    <p className="text-sm text-amber-700 mb-3">
                      The following employees were imported with missing data. They will be prompted to complete their profile on first login.
                    </p>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {results.incomplete_profiles.map((profile) => (
                        <div key={profile.employee_code} className="p-2 bg-white rounded border text-sm">
                          <span className="font-mono font-medium text-amber-800">{profile.employee_code}</span>
                          <span className="text-slate-500 ml-2">
                            Missing: {profile.missing_fields?.join(', ') || 'unknown'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Errors */}
                {results.errors && results.errors.length > 0 && (
                  <div>
                    <h4 className="font-medium text-red-700 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Errors ({results.errors.length})
                    </h4>
                    <div className="max-h-60 overflow-y-auto space-y-2">
                      {results.errors.map((err, idx) => (
                        <div key={`error-${err.row}-${idx}`} className="p-3 bg-red-50 rounded border border-red-200 text-sm">
                          <span className="font-medium">Row {err.row}:</span>{' '}
                          <span className="text-red-700">{err.error}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="pt-4 flex justify-between">
                  <Button variant="outline" onClick={resetWorkflow}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Import Another File
                  </Button>
                  <Button onClick={() => navigate('/employee-master')}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    View Employees
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default EmployeeMasterImport;
